import { getRequiredFields, removeEmptyFields, setEmptyFieldsToNull } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { getSchoolRedirectPath } from "@/routes/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect } from "react";
import {
  
  useUpdateSchoolInventoryDetailMutation,
  useGetSchoolInventoryDetailBySchoolIdQuery,
} from "@/services/school";
import { useCreateSchoolInventoryDetailMutation } from "@/services";
import {
  CURRICULUM_NOTES,
  INCLUDES_3D_PRINTER,
  INCLUDES_CHAIRS,
  INCLUDES_ELECTRICAL,
  INCLUDES_PAINTING,
  INCLUDES_PODIUM_MIC,
  INCLUDES_STORAGE_CABINETS,
  INCLUDES_TABLETS,
  INCLUDES_TEACHERS_TABLE,
  INCLUDES_TV,
  INFRASTRUCTURE_NOTES,
  OTHER_NOTES,
} from "./consts";
import { inventoryDetailsSchema, InventoryDetailsFormValues } from "./validations";

const useInventoryDetails = () => {
  const navigate = useNavigate();
  const { id, action } = useParams();
  
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const schoolId = String(id || "");

  const isEdit = action === "edit" && !!schoolId;

  // --- Form Setup for Inventory Details ---
  const inventoryDetailsForm = useForm<InventoryDetailsFormValues>({
    resolver: zodResolver(inventoryDetailsSchema),
    defaultValues: {
      [INCLUDES_TV]: false,
      [INCLUDES_3D_PRINTER]: false,
      [INCLUDES_PAINTING]: false,
      [INCLUDES_ELECTRICAL]: false,
      [INCLUDES_STORAGE_CABINETS]: false,
      [INCLUDES_TEACHERS_TABLE]: false,
      [INCLUDES_PODIUM_MIC]: false,
      [INCLUDES_TABLETS]: false,
      [INCLUDES_CHAIRS]: false,
      [INFRASTRUCTURE_NOTES]: "",
      [CURRICULUM_NOTES]: "",
      [OTHER_NOTES]: "",
    },
  });

  const requiredFields = getRequiredFields(inventoryDetailsSchema);

  // --- API Hooks ---
  const { mutateAsync: createInventoryDetail } =
    useCreateSchoolInventoryDetailMutation();
  const { mutateAsync: updateInventoryDetail } =
    useUpdateSchoolInventoryDetailMutation();

  // Fetch existing inventory details
  const {
    data: inventoryResp,
    isLoading: isLoadingExisting,
    refetch,
  } = useGetSchoolInventoryDetailBySchoolIdQuery(schoolId, {
    enabled: isEdit,
  } as any);

  // --- Prefill existing inventory details data in edit mode ---
  useEffect(() => {
    if (!isEdit || !inventoryResp?.data) return;
    const inv = inventoryResp.data;

    inventoryDetailsForm.reset({
      ...(inventoryDetailsForm.getValues() as any),
      [INCLUDES_TV]: Boolean(inv?.isTvIncluded ?? false),
      [INCLUDES_3D_PRINTER]: Boolean(inv?.is3DPrinterIncluded ?? false),
      [INCLUDES_PAINTING]: Boolean(inv?.isPaintingIncluded ?? false),
      [INCLUDES_ELECTRICAL]: Boolean(inv?.isElectricalIncluded ?? false),
      [INCLUDES_STORAGE_CABINETS]: Boolean(inv?.isStorageCabinetsIncluded ?? false),
      [INCLUDES_TEACHERS_TABLE]: Boolean(inv?.isTeachersTableIncluded ?? false),
      [INCLUDES_PODIUM_MIC]: Boolean(inv?.isPodiumIncluded ?? false),
      [INCLUDES_TABLETS]: Boolean(inv?.isTabletsIncluded ?? false),
      [INCLUDES_CHAIRS]: Boolean(inv?.isChairsIncluded ?? false),
      [INFRASTRUCTURE_NOTES]: String(inv?.infrastructureNotes ?? ""),
      [CURRICULUM_NOTES]: String(inv?.curriculumNotes ?? ""),
      [OTHER_NOTES]: String(inv?.otherNotes ?? ""),
    } as any);
  }, [isEdit, inventoryResp]);

  // --- Submit Handler for Inventory Details ---
  const onSubmitInventoryDetails = async (data: InventoryDetailsFormValues) => {
  const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;

  const basePayload = {
    isTvIncluded: Boolean(cleaned[INCLUDES_TV]),
    is3DPrinterIncluded: Boolean(cleaned[INCLUDES_3D_PRINTER]),
    isPaintingIncluded: Boolean(cleaned[INCLUDES_PAINTING]),
    isElectricalIncluded: Boolean(cleaned[INCLUDES_ELECTRICAL]),
    isStorageCabinetsIncluded: Boolean(cleaned[INCLUDES_STORAGE_CABINETS]),
    isTeachersTableIncluded: Boolean(cleaned[INCLUDES_TEACHERS_TABLE]),
    isPodiumIncluded: Boolean(cleaned[INCLUDES_PODIUM_MIC]),
    isTabletsIncluded: Boolean(cleaned[INCLUDES_TABLETS]),
    isChairsIncluded: Boolean(cleaned[INCLUDES_CHAIRS]),
    infrastructureNotes: cleaned[INFRASTRUCTURE_NOTES] || "",
    curriculumNotes: cleaned[CURRICULUM_NOTES] || "",
    otherNotes: cleaned[OTHER_NOTES] || "",
  };

  let payload;

  // --- If record exists, update it; otherwise create ---
  if (inventoryResp?.data?._id) {
    // ⛔️ Do NOT include schoolId for update
    payload = basePayload;
    await await updateInventoryDetail({
    schoolId, // ✅ include here for URL
    ...basePayload,
    });
  } else {
    // ✅ Include schoolId only for create
    payload = { schoolId, ...basePayload };
    await createInventoryDetail(payload);
  }

};


  const onCancel = () => {
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  return {
    inventoryDetailsForm,
    requiredFields,
    isLoadingExisting,
    onSubmitInventoryDetails,
    onCancel,
  };
};

export default useInventoryDetails;